import {commonEventTypeCd, commonTpCd} from "@COMPONENTS/common/constants";
import {getPrice} from "@COMPONENTS/common/eventUtil";
import {getExpectMileage} from "@COMPONENTS/common/goodsUtil";
import {formatForTagging, getTaggingData} from "@COMPONENTS/common/tagging/taggingCommon";
import {CouponPopType} from "@COMPONENTS/product/common/popup/desktop/CouponPopup";
import {EventDetail} from "@QUERIES/prdt/event/useEventDetail";
import {EventPrice} from "@QUERIES/prdt/event/useEventPrice";

/** 상세 데이터 */
export const reserveForTagging = (eventDetail: EventDetail) => {
	try {
		const {evCd, evNm, dayCnt, dayStayInfo, tourPatInfo} = eventDetail;
		const {continentCd, continentNm, countryCd, countryNm} = eventDetail;
		const goodsCd = evCd.split("-")[0];
		// USE_EVENT_PRICE
		const useEventPrice = getTaggingData("useEventPrice");
		const totalPrice = getPrice(useEventPrice);
		const price = {
			adtPrice: useEventPrice?.adtPrice ?? 0,
			chdPrice: useEventPrice?.chdPrice ?? 0,
			infPrice: useEventPrice?.infPrice ?? 0,
		};

		// USE_EVENT_MILEAGE
		const useEventMileage = getTaggingData("useEventMileage") || [];
		const mileage = getExpectMileage(useEventMileage);

		const {evStartDt, outDeprtTm, outArrvTm, outDayDiff, evArriveDt, inDeprtTm, inArrvTm, inDayDiff} = eventDetail;

		const outFlightDeprtTm = formatForTagging(evStartDt, outDeprtTm);
		const outFlightArrvTm = formatForTagging(evStartDt, outArrvTm, outDayDiff);
		const inFlightDeprtTm = formatForTagging(evArriveDt, inDeprtTm, -inDayDiff);
		const inFlightArrvTm = formatForTagging(evArriveDt, inArrvTm);

		return {
			prodDiv: "evCd",
			goodsCd,
			evCd,
			evNm,
			continentCd,
			continentNm,
			countryCd,
			countryNm,
			propCd: goodsCd.at(2) ?? "-",
			propNm: commonTpCd[goodsCd.at(2) ?? ""] ?? "-",
			dayCnt,
			nightCnt: Number(dayStayInfo?.split("박")[0]) ?? "-",
			tourInfo: tourPatInfo,
			outFlightCd: eventDetail?.outFlightNm ? eventDetail.outFlightNm.substring(0, 2) : "-",
			outFlightNm: eventDetail?.trCompanyNm ?? "-",
			outFlightDeprtTm: outFlightDeprtTm ?? "-",
			outFlightArrvTm: outFlightArrvTm ?? "-",
			inFlightCd: eventDetail?.inFlightNm ? eventDetail.inFlightNm.substring(0, 2) : "-",
			inFlightNm: eventDetail?.trCompanyNm ?? "-",
			inFlightDeprtTm: inFlightDeprtTm ?? "-",
			inFlightArrvTm: inFlightArrvTm ?? "-",
			progCd: eventDetail?.evProgDivi ?? "-",
			progNm: eventDetail?.evProgDiviNm ?? "-",
			recCd: eventDetail?.eventTypeCd ?? "-",
			recNm: commonEventTypeCd[eventDetail?.eventTypeCd] ?? "-",
			adultPrice: price.adtPrice,
			childPrice: price.chdPrice,
			infantPrice: price.infPrice,
			adultSalePrice: totalPrice.adult,
			childSalePrice: totalPrice.child,
			infantSalePrice: totalPrice.infant,
			expectPoint: mileage,
		};
	} catch (error) {
		return null;
	}
};

/**
 * braze, amplitude
 * 	click	pkg_select_evCd_reserve
 * @description [예약하기] 버튼 클릭
 */
export const taggingClickReserve = (
	props: {eventDetail: EventDetail} & {count: {adultCnt: number; childCnt: number; infantCnt: number}} & {
		coupon: CouponPopType[];
	},
) => {
	try {
		const {eventDetail, count, coupon = []} = props;
		const reserve = reserveForTagging(eventDetail);

		// 쿠폰
		const couponCd = coupon.map(c => c.couponNo).join(",") || "-";
		const couponNm = coupon.map(c => c.couponNm).join(",") || "-";
		const couponDiscDivi = coupon.map(c => c.discDiviCd).join(",") || "-";
		const couponPrice = coupon.map(c => c.calcObj.totalPrice).join(",") || "-";

		// 인원수, 금액
		const {adultCnt, childCnt, infantCnt} = count;
		const totalCnt = adultCnt + childCnt + infantCnt;
		const finPrice =
			reserve.adultSalePrice * adultCnt + reserve.childSalePrice * childCnt + reserve.infantSalePrice * infantCnt;
		return {
			...reserve,
			adultCnt,
			childCnt,
			infantCnt,
			couponCd,
			couponNm,
			couponDiscDivi,
			couponPrice,
			finPrice,
			totalCnt,
		};
	} catch (error) {
		return {};
	}
};

/**
 * braze, amplitude
 *      pkg_booking_complete
 * @description 예약 완료 페이지 LOAD, REVENUE
 */
export const taggingClickPrebookingComplete = (
	props: {eventDetail: EventDetail} & {count: {adultCnt: number; childCnt: number; infantCnt: number}} & {
		coupon: CouponPopType[];
		rervNo: string;
	},
) => {
	try {
		const {eventDetail, count, coupon = [], rervNo} = props;
		const reserve = reserveForTagging(eventDetail);

		// USE_EVENT_PRICE
		const useEventPrice = getTaggingData("useEventPrice") as EventPrice;

		// 쿠폰
		const couponCd = coupon.map(c => c.couponNo).join(",") || "-";
		const couponNm = coupon.map(c => c.couponNm).join(",") || "-";
		const couponDiscDivi = coupon.map(c => c.discDiviCd).join(",") || "-";
		const couponPrice = coupon.reduce((acc, cur) => acc + cur.calcObj.totalPrice, 0); // 쿠폰 총액

		// 인원수, 금액
		const {adultCnt, childCnt, infantCnt} = count;
		const totalCnt = adultCnt + childCnt + infantCnt;
		const finPrice =
			reserve.adultSalePrice * adultCnt + reserve.childSalePrice * childCnt + reserve.infantSalePrice * infantCnt;

		const fixRervCd = eventDetail.fixRervCd ?? "DF";
		const fixRervNDType = fixRervCd === "ND" ? useEventPrice.depositDivi ?? "-" : "-";
		const fixRervNDPrice = fixRervCd === "ND" ? useEventPrice.depositAmt ?? 0 : 0;

		const data = {
			...reserve,
			adultCnt,
			childCnt,
			infantCnt,
			couponCd,
			couponNm,
			couponDiscDivi,
			couponPrice, // 쿠폰 할인가
			finPrice, // 총 판매가 (할인금액 제외)
			totalCnt, // 예약 인원
			fixRervCd,
			fixRervNDType,
			fixRervNDPrice,
		};

		return {
			data,
			purchase: {
				productId: "pkg_prebooking_complete", // revenue 와 다름
				price: finPrice, // 총판매가 (할인금액 제외)
				currencyCode: "KRW",
				quantity: totalCnt, // 예약 인원
				purchaseProperties: {
					...data,
					cat: "PKG",
					rervNo,
					couponCd,
					couponNm,
					couponDiscDivi,
					couponPrice,
				},
			},
			revenue: {
				productId: rervNo, // 예약코드
				price: finPrice, // 총판매가 (할인금액 제외)
				quantity: totalCnt, // 예약 인원
				revenue: finPrice - couponPrice, // 총판매가 (할인금액 포함)
			},
		};
	} catch (error) {
		return {};
	}
};
